import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Flame, Sparkles, Utensils, ChevronDown, ChevronUp, BrainCircuit, Loader2 } from "lucide-react";
import { supabase } from "../lib/supabaseClient";

// ----------------------
// PLAN DATABASE
// ----------------------
const PLANS = {
  goals: {
    weight_loss: {
      name: "Metabolic Fat-Loss Protocol",
      calories: 1800,
      description: "A precision calorie-restricted plan designed to optimize fat oxidation while preserving lean muscle mass through high-thermogenic foods.",
      meals: {
        veg: ["Breakfast: Overnight Oats with Chia & Berries", "Lunch: Grilled Paneer & Spinach Power Salad", "Snack: Roasted Makhana with Green Tea", "Dinner: Yellow Dal Tadka with Cauliflower Rice"],
        mixed: ["Breakfast: Masala Oats with 2 Egg Whites", "Lunch: Mediterranean Egg & Avocado Salad", "Snack: Greek Yogurt with Flaxseeds", "Dinner: Lemon-Grilled Chicken with Steamed Broccoli"],
        "non-veg": ["Breakfast: Egg White Sandwich on Whole Grain", "Lunch: Grilled Chicken Breast with Seasonal Greens", "Snack: Smoked Turkey Slices with Almonds", "Dinner: Baked Fish with Herb-Infused Asparagus"],
      },
    },
    muscle_gain: {
      name: "Hypertrophy & Performance Plan",
      calories: 2600,
      description: "Calorie-surplus strategy focused on high-quality protein synthesis and complex carbohydrate loading to fuel intense strength training.",
      meals: {
        veg: ["Breakfast: Paneer Bhurji with 2 Multigrain Parathas", "Lunch: High-Protein Soya Chunks & Quinoa Bowl", "Post-Workout: Peanut Butter & Banana Smoothie", "Dinner: Paneer Tikka Masala with Brown Rice"],
        mixed: ["Breakfast: 3 Whole Egg Omelette with Sourdough", "Lunch: Grilled Chicken, Paneer & Brown Rice Bowl", "Post-Workout: Whey Protein & Fruit Bowl", "Dinner: Egg Curry with Ghee-Tossed Quinoa"],
        "non-veg": ["Breakfast: Scrambled Eggs with Chicken Sausages", "Lunch: Lean Beef or Chicken Roll with Hummus", "Post-Workout: Protein Shake with Creatine-Rich Fruit", "Dinner: Grilled Salmon or Mutton Curry with Steamed Rice"],
      },
    },
    maintain: {
      name: "Homeostatic Maintenance Plan",
      calories: 2100,
      description: "A balanced macronutrient profile (40/30/30) designed to sustain current weight, support cognitive function, and maintain hormonal health.",
      meals: {
        veg: ["Breakfast: Vegetable Poha with Peanuts", "Lunch: Paneer Tikka & Chickpea Salad Bowl", "Snack: Seasonal Fruit with Walnuts", "Dinner: Whole Wheat Chapatis with Mixed Dal & Veggies"],
        mixed: ["Breakfast: Vegetable Poha with Boiled Eggs", "Lunch: Grilled Chicken & Lentil Soup", "Snack: Handful of Trail Mix", "Dinner: Egg Bhurji with Whole Wheat Roti"],
        "non-veg": ["Breakfast: Eggs over Easy on Whole Wheat Toast", "Lunch: Chicken Quinoa Bowl with Pesto", "Snack: Apple with Almond Butter", "Dinner: Steamed Fish with Sweet Potato Puree"],
      },
    },
  },

  conditions: {
    pcos: {
      name: "Hormonal & Insulin Regulation Diet",
      calories: 1600,
      description: "Low-Glycemic Index (GI) approach integrated with anti-inflammatory nutrients to manage androgen levels and improve insulin sensitivity.",
      meals: {
        veg: ["Breakfast: Chia Seed Pudding with Berries", "Lunch: Tofu Stir-fry with Broccoli & Bell Peppers", "Snack: Pumpkin Seeds & Green Tea", "Dinner: Moong Dal with Sautéed Spinach"],
        mixed: ["Breakfast: Chia Bowl with Soft Boiled Eggs", "Lunch: Egg & Avocado Power Salad", "Snack: Walnuts & Hibiscus Tea", "Dinner: Grilled Chicken with Zucchini Noodles"],
        "non-veg": ["Breakfast: 3 Egg White Omelette with Spinach", "Lunch: Grilled Salmon with Asparagus", "Snack: Almonds & Berries", "Dinner: Lemon Chicken with Steamed Green Beans"],
      },
    },
    diabetes: {
      name: "Glycemic Control & Glucose Stability Diet",
      calories: 1650,
      description: "Low-carbohydrate, high-fiber dietary pattern specifically formulated to prevent postprandial glucose spikes and support HbA1c management.",
      meals: {
        veg: ["Breakfast: Moong Dal Chilla with Mint Chutney", "Lunch: Paneer & Cucumber Salad with Lemon Dressing", "Snack: Celery Sticks with Hummus", "Dinner: Mixed Veggie Stew with Low-Carb Flour Roti"],
        mixed: ["Breakfast: Moong Chilla with 2 Boiled Eggs", "Lunch: Grilled Chicken Breast with Salad", "Snack: Hard Boiled Egg with Black Pepper", "Dinner: Baked Fish with Steamed Greens"],
        "non-veg": ["Breakfast: Egg White Scramble with Peppers", "Lunch: Grilled Chicken Salad with Olive Oil", "Snack: Handful of Pistachios", "Dinner: Steamed Fish with Sautéed Garlic Broccoli"],
      },
    },
    thyroid: {
      name: "Metabolic Support & Endocrine Diet",
      calories: 1700,
      description: "Nutrient-dense plan rich in Selenium, Iodine, and Tyrosine to support thyroid hormone production and boost overall metabolic rate.",
      meals: {
        veg: ["Breakfast: Idli Sambar with Coconut Chutney", "Lunch: Paneer & Bell Pepper Skewers", "Snack: Brazil Nuts (Natural Selenium Source)", "Dinner: Rajma (Kidney Beans) with Brown Rice"],
        mixed: ["Breakfast: 2 Whole Eggs with Sambar", "Lunch: Fish Cutlets with Green Salad", "Snack: Yogurt with Sunflower Seeds", "Dinner: Egg Curry with Steamed Cauliflower"],
        "non-veg": ["Breakfast: Boiled Eggs with Sliced Avocado", "Lunch: Grilled Fish with Lemon & Garlic", "Snack: Turkey Jerky or Almonds", "Dinner: Roasted Chicken with Root Vegetables"],
      },
    },
    hypertension: {
      name: "DASH-Aligned Heart Health Diet",
      calories: 1700,
      description: "Low-sodium, high-potassium cardiovascular support plan focused on endothelial health and blood pressure regulation.",
      meals: {
        veg: ["Breakfast: Steel-Cut Oats with Banana & Walnuts", "Lunch: Sprouted Moong Salad with Pomegranate", "Snack: Unsalted Roasted Chickpeas", "Dinner: Bottle Gourd (Lauki) Sabzi with 1 Roti"],
        mixed: ["Breakfast: Oats with 1 Egg White", "Lunch: Steamed Veggies with 2 Hard Boiled Eggs", "Snack: Low-Fat Yogurt", "Dinner: Baked Fish (No Added Salt) with Steamed Veggies"],
        "non-veg": ["Breakfast: 2 Egg Whites with Steamed Spinach", "Lunch: Grilled Chicken (Low Sodium Marinade)", "Snack: Sliced Cucumber with Lime", "Dinner: Fish Stew with Tomatoes & Herbs"],
      },
    },
    "pre-diabetes": {
      name: "Insulin Sensitivity Reversal Diet",
      calories: 1650,
      description: "Strategic carbohydrate distribution plan aimed at reversing insulin resistance through fiber-rich, low-GI whole foods.",
      meals: {
        veg: ["Breakfast: Ragi (Millet) Dosa with Peanut Chutney", "Lunch: Paneer & Quinoa Tabbouleh", "Snack: Flaxseed Crackers", "Dinner: Vegetable Upma with Semolina & Pea Protein"],
        mixed: ["Breakfast: Ragi Dosa with Poached Egg", "Lunch: Chicken & Chickpea Power Bowl", "Snack: Green Apple with Cinnamon", "Dinner: Egg White Curry with Veggie Salad"],
        "non-veg": ["Breakfast: Egg White Omelette with Mushrooms", "Lunch: Grilled Chicken Salad with Balsamic", "Snack: Roasted Soy Nuts", "Dinner: Baked Fish with Turmeric-Roasted Cauliflower"],
      },
    },
    cholesterol: {
      name: "Cardiovascular Lipid-Management Diet",
      calories: 1600,
      description: "Soluble fiber-focused plan enriched with Omega-3 fatty acids to reduce LDL cholesterol and improve the overall lipid profile.",
      meals: {
        veg: ["Breakfast: Oats with Sliced Apple & Cinnamon", "Lunch: Sprouts & Kale Salad with Walnuts", "Snack: Garlic-Roasted Makhana", "Dinner: Palak Dal (Spinach Lentils) with 1 Roti"],
        mixed: ["Breakfast: Oats with 2 Egg Whites", "Lunch: Grilled Salmon with Quinoa", "Snack: 1/2 Avocado with Black Pepper", "Dinner: Moong Dal Soup with Steamed Veggies"],
        "non-veg": ["Breakfast: 3 Boiled Egg Whites", "Lunch: Grilled Mackerel or Salmon Salad", "Snack: Handful of Almonds", "Dinner: Clear Chicken Soup with High-Fiber Veggies"],
      },
    },
  },
};

const choosePlan = (profile) => {
  const condition = profile.health_conditions?.toLowerCase() || "none";
  const goal = profile.goal?.replace("-", "_") || "weight_loss";
  const pref = profile.food_preference?.toLowerCase() || "veg";

  if (condition !== "none" && PLANS.conditions[condition]) {
    const plan = PLANS.conditions[condition];
    return {
      ...plan,
      meals: plan.meals[pref] || plan.meals.veg || Object.values(plan.meals)[0],
    };
  }

  const plan = PLANS.goals[goal] || PLANS.goals.weight_loss;
  return {
    ...plan,
    meals: plan.meals[pref] || plan.meals.veg,
  };
};

const AutoDietPlan = ({ userId }) => {
  const [plan, setPlan] = useState(null);
  const [open, setOpen] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);

  const handleGenerateDiet = async () => {
    setIsGenerating(true);
    
    // Simulate AI Processing delay
    setTimeout(async () => {
      const { data } = await supabase
        .from("profiles")
        .select("goal, health_conditions, food_preference")
        .eq("id", userId)
        .single();

      if (data) {
        const profile = {
          goal: data.goal || "weight_loss",
          health_conditions: data.health_conditions || "none",
          food_preference: data.food_preference || "veg",
        };
        setPlan(choosePlan(profile));
      }
      setIsGenerating(false);
    }, 1500);
  };

  return (
    <div className="max-w-3xl mx-auto py-3 px-3 sm:py-6 sm:px-4">
      <AnimatePresence mode="wait">
        {!plan ? (
          <motion.div
            key="generate-view"
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.95 }}
            className="bg-white p-5 rounded-[28px] shadow-lg border border-emerald-50 text-center"

          >
            <div className="w-20 h-20 bg-emerald-50 rounded-3xl flex items-center justify-center mx-auto mb-6">
              <BrainCircuit className="w-10 h-10 text-emerald-600" />
            </div>
            <h2 className="text-3xl font-black text-gray-900 mb-4">AI Diet Generator</h2>
            <p className="text-gray-500 mb-8 max-w-xs mx-auto">
              Our AI will analyze your health conditions, fitness goals, and food preferences to create a custom meal plan.
            </p>
            
            <button
              onClick={handleGenerateDiet}
              disabled={isGenerating}
              className="w-full bg-emerald-600 hover:bg-emerald-700 disabled:bg-emerald-300 text-white font-black py-5 rounded-2xl flex items-center justify-center gap-3 transition-all shadow-lg shadow-emerald-100 uppercase tracking-widest text-sm"
            >
              {isGenerating ? (
                <>
                  <Loader2 className="animate-spin" /> Analyzing Profile...
                </>
              ) : (
                <>
                  <Sparkles className="w-5 h-5" /> Generate My Diet Plan
                </>
              )}
            </button>
          </motion.div>
        ) : (
          <motion.div
            key="plan-view"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-white p-8 rounded-[40px] shadow-2xl border border-gray-50"
          >
            {/* AI Banner */}
            <div className="bg-gradient-to-r from-emerald-600 to-teal-600 text-white p-6 rounded-3xl mb-8 flex items-center gap-4">
              <Sparkles className="w-8 h-8 text-yellow-300" />
              <div>
                <h2 className="text-xl font-black uppercase tracking-tight">Personalized Diet</h2>
                <p className="text-emerald-50 text-xs font-bold opacity-80 uppercase tracking-widest">Optimized by AI</p>
              </div>
            </div>

            {/* Title */}
            <div className="mb-6">
              <h3 className="text-2xl font-black text-gray-900">{plan.name}</h3>
              <p className="text-gray-500 mt-2 font-medium leading-relaxed">{plan.description}</p>
            </div>

            {/* Calories */}
            <div className="flex items-center gap-2 bg-orange-50 px-5 py-3 rounded-2xl w-fit mb-8 border border-orange-100">
              <Flame className="text-orange-600 w-5 h-5" />
              <span className="font-black text-orange-700 text-sm uppercase tracking-wider">{plan.calories} kcal / Day</span>
            </div>

            {/* Collapsible Meals */}
            <div className="border-t border-gray-100 pt-8">
              <button 
                onClick={() => setOpen(!open)} 
                className="flex justify-between items-center w-full group"
              >
                <span className="font-black text-gray-900 flex items-center gap-3 uppercase tracking-widest text-xs">
                  <Utensils className="text-emerald-600" /> View Daily Meals
                </span>
                <div className={`p-2 rounded-xl transition-colors ${open ? 'bg-emerald-50 text-emerald-600' : 'bg-gray-50 text-gray-400'}`}>
                  {open ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
                </div>
              </button>

              <AnimatePresence>
                {open && (
                  <motion.div
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: "auto" }}
                    exit={{ opacity: 0, height: 0 }}
                    className="mt-6 space-y-4 overflow-hidden"
                  >
                    {plan.meals.map((meal, i) => (
                      <motion.div 
                        initial={{ opacity: 0, x: -10 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: i * 0.1 }}
                        key={i} 
                        className="p-5 bg-gray-50 rounded-2xl border border-gray-100 flex items-center gap-4"
                      >
                        <div className="w-8 h-8 rounded-lg bg-white shadow-sm flex items-center justify-center text-xs font-black text-emerald-600 border border-emerald-50">
                          {i + 1}
                        </div>
                        <span className="text-gray-700 font-bold">{meal}</span>
                      </motion.div>
                    ))}
                    
                   
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default AutoDietPlan;